import React from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../context/FeedbackContext';
import SelectField from '../inputs/SelectField';
import ClientTagChip from '../clients/ClientTagChip';
import {
  buildClientTagRulesSavePayload,
  conditionNeedsValue,
  createEmptyClientTagRule,
  operatorsForRuleField,
  CLIENT_TAG_RULE_FIELDS,
} from '../../utils/clientTags';

function ConditionValueField({ condition, onChange, t }) {
  const { field, operator, value } = condition;

  if (!conditionNeedsValue(operator)) {
    return null;
  }

  if (field === 'disabled') {
    return (
      <TextField
        select
        label={t('settings.clientTagRules.value')}
        value={value === true || value === 'true' ? 'true' : 'false'}
        onChange={(e) => onChange({ ...condition, value: e.target.value === 'true' })}
        size="small"
        sx={{ minWidth: 120 }}
      >
        <MenuItem value="true">{t('common.yes')}</MenuItem>
        <MenuItem value="false">{t('common.no')}</MenuItem>
      </TextField>
    );
  }

  if (field === 'language') {
    return (
      <TextField
        select
        label={t('settings.clientTagRules.value')}
        value={value || 'en'}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
        size="small"
        sx={{ minWidth: 120 }}
      >
        <MenuItem value="en">English</MenuItem>
        <MenuItem value="he">Hebrew</MenuItem>
        <MenuItem value="ru">Russian</MenuItem>
        <MenuItem value="es">Spanish</MenuItem>
      </TextField>
    );
  }

  if (field === 'unassignedItemsCount' || field === 'openChecksCount' || field === 'reservationLimit') {
    return (
      <TextField
        label={t('settings.clientTagRules.value')}
        value={value ?? ''}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
        type="number"
        size="small"
        sx={{ minWidth: 100 }}
      />
    );
  }

  return (
    <TextField
      label={t('settings.clientTagRules.value')}
      value={value ?? ''}
      onChange={(e) => onChange({ ...condition, value: e.target.value })}
      size="small"
      sx={{ minWidth: 140 }}
    />
  );
}

export default function ClientTagRulesSettingsPanel({
  form,
  setField,
  onSave,
  onReset,
  saving,
}) {
  const { t } = useTranslation();
  const { confirm } = useFeedback();
  const rules = form.clientTagRules || [];
  const tags = form.clientTags || [];

  const tagOptions = tags.map((tag) => ({ value: tag._id, label: tag.name, color: tag.color }));

  const updateRules = (next) => setField('clientTagRules', next);

  const handleAddRule = () => {
    const defaultTagId = tags[0]?._id || '';
    updateRules([...rules, createEmptyClientTagRule(defaultTagId)]);
  };

  const handleDeleteRule = async (index) => {
    const ok = await confirm({
      title: t('settings.clientTagRules.deleteTitle'),
      message: t('settings.clientTagRules.deleteConfirm'),
      confirmLabel: t('common.delete'),
      confirmColor: 'error',
    });
    if (!ok) return;
    updateRules(rules.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave(buildClientTagRulesSavePayload(rules));
  };

  const fieldLabel = (field) => t(`settings.clientTagRules.fields.${field}`);
  const operatorLabel = (op) => t(`settings.clientTagRules.operators.${op}`);

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        {t('settings.clientTagRules.description')}
      </Typography>

      {tags.length === 0 && (
        <Typography variant="body2" color="warning.main">
          {t('settings.clientTagRules.noTagsHint')}
        </Typography>
      )}

      {rules.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          {t('settings.clientTagRules.empty')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {rules.map((rule, ruleIndex) => (
            <Box
              key={rule._id}
              sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} mb={1.5}>
                <SelectField
                  label={t('settings.clientTagRules.applyTag')}
                  options={tagOptions}
                  value={rule.tagId || ''}
                  onChange={(tagId) => {
                    const next = rules.map((r, i) => (i === ruleIndex ? { ...r, tagId } : r));
                    updateRules(next);
                  }}
                  fullWidth
                  disabled={tagOptions.length === 0}
                  emptyLabel={t('settings.clientTagRules.selectTag')}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={rule.enabled !== false}
                      onChange={(e) => {
                        const next = rules.map((r, i) => (i === ruleIndex ? { ...r, enabled: e.target.checked } : r));
                        updateRules(next);
                      }}
                      size="small"
                    />
                  )}
                  label={t('settings.clientTagRules.enabled')}
                />
                <IconButton color="error" onClick={() => handleDeleteRule(ruleIndex)} aria-label={t('common.delete')}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>

              {rule.tagId && tags.find((x) => String(x._id) === String(rule.tagId)) && (
                <Box sx={{ mb: 1 }}>
                  <ClientTagChip tag={tags.find((x) => String(x._id) === String(rule.tagId))} />
                </Box>
              )}

              <Stack spacing={1}>
                {(rule.conditions || []).map((condition, condIndex) => (
                  <Stack
                    key={`${rule._id}-${condIndex}`}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                  >
                    <SelectField
                      label={t('settings.clientTagRules.field')}
                      options={CLIENT_TAG_RULE_FIELDS.map((f) => ({ value: f, label: fieldLabel(f) }))}
                      value={condition.field}
                      onChange={(field) => {
                        const ops = operatorsForRuleField(field);
                        const nextCond = { field, operator: ops[0], value: '' };
                        const nextRules = rules.map((r, i) => {
                          if (i !== ruleIndex) return r;
                          const nextConditions = [...(r.conditions || [])];
                          nextConditions[condIndex] = nextCond;
                          return { ...r, conditions: nextConditions };
                        });
                        updateRules(nextRules);
                      }}
                      sx={{ minWidth: 180 }}
                    />
                    <SelectField
                      label={t('settings.clientTagRules.operator')}
                      options={operatorsForRuleField(condition.field).map((op) => ({ value: op, label: operatorLabel(op) }))}
                      value={condition.operator}
                      onChange={(operator) => {
                        const nextRules = rules.map((r, i) => {
                          if (i !== ruleIndex) return r;
                          const nextConditions = [...(r.conditions || [])];
                          nextConditions[condIndex] = { ...condition, operator };
                          return { ...r, conditions: nextConditions };
                        });
                        updateRules(nextRules);
                      }}
                      sx={{ minWidth: 160 }}
                    />
                    <ConditionValueField
                      condition={condition}
                      onChange={(nextCond) => {
                        const nextRules = rules.map((r, i) => {
                          if (i !== ruleIndex) return r;
                          const nextConditions = [...(r.conditions || [])];
                          nextConditions[condIndex] = nextCond;
                          return { ...r, conditions: nextConditions };
                        });
                        updateRules(nextRules);
                      }}
                      t={t}
                    />
                    <IconButton
                      color="error"
                      disabled={(rule.conditions || []).length <= 1}
                      onClick={() => {
                        const nextRules = rules.map((r, i) => {
                          if (i !== ruleIndex) return r;
                          return { ...r, conditions: r.conditions.filter((_, ci) => ci !== condIndex) };
                        });
                        updateRules(nextRules);
                      }}
                      aria-label={t('common.delete')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>

              <Button
                size="small"
                startIcon={<AddIcon />}
                sx={{ mt: 1 }}
                onClick={() => {
                  const nextRules = rules.map((r, i) => {
                    if (i !== ruleIndex) return r;
                    return {
                      ...r,
                      conditions: [...(r.conditions || []), { field: 'openChecksCount', operator: 'gt', value: 0 }],
                    };
                  });
                  updateRules(nextRules);
                }}
              >
                {t('settings.clientTagRules.addCondition')}
              </Button>
            </Box>
          ))}
        </Stack>
      )}

      <Button
        startIcon={<AddIcon />}
        onClick={handleAddRule}
        variant="outlined"
        size="small"
        sx={{ alignSelf: 'flex-start' }}
        disabled={tags.length === 0}
      >
        {t('settings.clientTagRules.addRule')}
      </Button>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
        <Button variant="outlined" onClick={onReset} disabled={saving}>{t('common.reset')}</Button>
      </Stack>
    </Stack>
  );
}
